import React, { useRef } from 'react';

import styled from 'styled-components';

const RawDetails = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.borderLine};
  border-radius: 4px;
  padding: 8px 12px;

  & + & {
    margin-top: 8px;
  }

  summary {
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textDefault};
    user-select: none;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::before {
    content: '▸';
    font-size: 10px;
    color: ${({ theme }) => theme.colors.textSubdued};
    transition: transform 0.15s ease;
  }

  &[open] summary::before {
    transform: rotate(90deg);
  }
`;

const RawJsonContainer = styled.div`
  margin: 8px 0 0;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.borderLine};
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textDefault};
  max-height: 320px;
  overflow: auto;
  word-break: break-word;
`;

const TreeToolbar = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

const TreeButton = styled.button`
  appearance: none;
  border: 1px solid ${({ theme }) => theme.colors.borderLine};
  background: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.textDefault};
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundDark};
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const TreeNode = styled.details`
  summary {
    cursor: pointer;
    list-style: none;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::before {
    content: '▸';
    font-size: 9px;
    color: ${({ theme }) => theme.colors.textSubdued};
    transition: transform 0.1s ease;
  }

  &[open] > summary::before {
    transform: rotate(90deg);
  }
`;

const TreeChildren = styled.div`
  padding-left: 14px;
  border-left: 1px dashed ${({ theme }) => theme.colors.borderLine};
  margin-left: 4px;
`;

const TreeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: baseline;
`;

const TreeKey = styled.span`
  color: ${({ theme }) => theme.colors.textDefault};
  font-weight: 600;

  &::after {
    content: ':';
    margin-right: 2px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.textSubdued};
  }
`;

export const TreePreview = styled.span`
  color: ${({ theme }) => theme.colors.textDisabled};
  font-style: italic;
`;

const TreePrimitiveString = styled.span`
  color: ${({ theme }) => theme.colors.palette.green || '#0a7d3b'};
`;

const TreePrimitiveNumber = styled.span`
  color: ${({ theme }) => theme.colors.palette.blue};
`;

const TreePrimitiveBoolean = styled.span`
  color: ${({ theme }) => theme.colors.palette.purple || '#8842c2'};
  font-weight: 600;
`;

// Cap recursion depth so a deeply-nested (but non-cyclic) payload doesn't
// stall the render. Adjust if real data starts hitting this regularly.
const MAX_TREE_DEPTH = 15;

const isExpandable = (
  value: unknown
): value is Record<string, unknown> | unknown[] =>
  value !== null && typeof value === 'object';

const renderPrimitive = (value: unknown): React.ReactNode => {
  if (value === null) {
    return <TreePreview>null</TreePreview>;
  }
  if (value === undefined) {
    return <TreePreview>undefined</TreePreview>;
  }
  switch (typeof value) {
    case 'string':
      return <TreePrimitiveString>&quot;{value}&quot;</TreePrimitiveString>;
    case 'number':
      return <TreePrimitiveNumber>{value}</TreePrimitiveNumber>;
    case 'boolean':
      return <TreePrimitiveBoolean>{String(value)}</TreePrimitiveBoolean>;
    default:
      return <span>{String(value)}</span>;
  }
};

interface JsonNodeProps {
  keyName?: string;
  value: unknown;
  // Ancestor objects on the current recursion path. Used to short-circuit
  // circular references (CMS asset items can chain container ↔ parent ↔
  // children, which would otherwise infinite-loop the recursive render —
  // native <details> still mounts collapsed children in the DOM).
  ancestors?: Set<object>;
  depth?: number;
}

const JsonNode = ({ keyName, value, ancestors, depth = 0 }: JsonNodeProps) => {
  if (!isExpandable(value)) {
    return (
      <TreeRow>
        {keyName !== undefined && <TreeKey>{keyName}</TreeKey>}
        {renderPrimitive(value)}
      </TreeRow>
    );
  }

  if (ancestors && ancestors.has(value as object)) {
    return (
      <TreeRow>
        {keyName !== undefined && <TreeKey>{keyName}</TreeKey>}
        <TreePreview>[Circular]</TreePreview>
      </TreeRow>
    );
  }

  if (depth >= MAX_TREE_DEPTH) {
    return (
      <TreeRow>
        {keyName !== undefined && <TreeKey>{keyName}</TreeKey>}
        <TreePreview>[Max depth reached]</TreePreview>
      </TreeRow>
    );
  }

  const isArray = Array.isArray(value);
  // Arrays preserve their natural index order; objects are alphabetized by
  // key for predictable scanning across renders.
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
        a.localeCompare(b)
      );

  const childAncestors = new Set(ancestors);
  childAncestors.add(value as object);

  if (entries.length === 0) {
    return (
      <TreeRow>
        {keyName !== undefined && <TreeKey>{keyName}</TreeKey>}
        <TreePreview>{isArray ? '[]' : '{}'}</TreePreview>
      </TreeRow>
    );
  }

  const summaryLabel = isArray
    ? `Array(${entries.length})`
    : `{ ${entries.length} ${entries.length === 1 ? 'key' : 'keys'} }`;

  return (
    <TreeNode>
      <summary>
        {keyName !== undefined && <TreeKey>{keyName}</TreeKey>}
        <TreePreview>{summaryLabel}</TreePreview>
      </summary>
      <TreeChildren>
        {entries.map(([k, v]) => (
          <JsonNode
            key={k}
            keyName={k}
            value={v}
            ancestors={childAncestors}
            depth={depth + 1}
          />
        ))}
      </TreeChildren>
    </TreeNode>
  );
};

interface RawDataDropdownProps {
  label: string;
  value: unknown;
  emptyHint?: string;
}

export const RawDataDropdown = ({
  label,
  value,
  emptyHint,
}: RawDataDropdownProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setAllOpen = (open: boolean) => {
    const root = containerRef.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('details').forEach((node) => {
      (node as HTMLDetailsElement).open = open;
    });
  };

  const isObject =
    value !== null && value !== undefined && typeof value === 'object';
  const isArrayValue = Array.isArray(value);
  const rawEntries = isObject
    ? Object.entries(value as Record<string, unknown>)
    : [];
  // Arrays preserve their natural index order; objects are alphabetized for
  // predictable scanning across renders.
  const entries = isArrayValue
    ? rawEntries
    : rawEntries.sort(([a], [b]) => a.localeCompare(b));
  const hasContent = isObject ? entries.length > 0 : value !== undefined;

  return (
    <RawDetails>
      <summary>{label}</summary>
      {hasContent ? (
        <>
          <TreeToolbar>
            <TreeButton type="button" onClick={() => setAllOpen(true)}>
              Expand all
            </TreeButton>
            <TreeButton type="button" onClick={() => setAllOpen(false)}>
              Collapse all
            </TreeButton>
          </TreeToolbar>
          <RawJsonContainer ref={containerRef}>
            {isObject ? (
              (() => {
                // Seed ancestor set with the root value so top-level
                // self-references (rare, but possible in CMS data) get
                // caught as [Circular] instead of recursing.
                const rootAncestors = new Set<object>([value as object]);
                return entries.map(([k, v]) => (
                  <JsonNode
                    key={k}
                    keyName={k}
                    value={v}
                    ancestors={rootAncestors}
                    depth={1}
                  />
                ));
              })()
            ) : (
              <JsonNode value={value} />
            )}
          </RawJsonContainer>
        </>
      ) : (
        <RawJsonContainer>
          <TreePreview>{emptyHint || 'No data'}</TreePreview>
        </RawJsonContainer>
      )}
    </RawDetails>
  );
};
